/**
 * Analyse de la structure des titres (H1–H6).
 * Source partagée : modale SEO détail, fiches pages de référence.
 */

export const HEADING_LEVEL_BORDER = {
  1: 'border-l-indigo-500 bg-indigo-50/50',
  2: 'border-l-blue-500 bg-blue-50/30',
  3: 'border-l-cyan-500 bg-cyan-50/20',
  4: 'border-l-teal-500 bg-teal-50/20',
  5: 'border-l-green-500 bg-green-50/20',
  6: 'border-l-emerald-500 bg-emerald-50/20',
};

export const HEADING_LEVEL_TAG = {
  1: 'bg-indigo-100 text-indigo-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-cyan-100 text-cyan-700',
  4: 'bg-teal-100 text-teal-700',
  5: 'bg-green-100 text-green-700',
  6: 'bg-emerald-100 text-emerald-700',
};

export function deriveHeadingsCounts(headingsList = []) {
  const counts = {};
  for (const h of headingsList) {
    const key = `h${h.level}`;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

/** Liste ordonnée + compteurs à partir d'un audit SEO. */
export function resolveHeadingsData(seo, h1Fallback = '') {
  const am = seo?.advanced_metrics || {};
  let headingsList = Array.isArray(am.headings_list) ? am.headings_list : [];
  if (!headingsList.length && h1Fallback) {
    headingsList = [{ tag: 'h1', text: h1Fallback, level: 1 }];
  }
  const fromSeo = seo?.headings && Object.keys(seo.headings).length > 0;
  const headingsCounts = fromSeo ? seo.headings : deriveHeadingsCounts(headingsList);
  return { headingsList, headingsCounts, hasFullData: headingsList.length > 0 };
}

export function analyzeHeadings(headingsList = [], headingsCounts = {}) {
  const issues = [];
  const strengths = [];
  const contentRecs = [];

  const h1Count = headingsCounts.h1 || 0;
  const h2Count = headingsCounts.h2 || 0;
  const h3Count = headingsCounts.h3 || 0;

  if (h1Count === 0) {
    issues.push({
      severity: 'error',
      message: 'Aucun H1 détecté — chaque page doit avoir exactement un H1 unique.',
      fix: 'Ajoutez un seul H1 qui résume le sujet principal de la page.',
    });
  } else if (h1Count > 1) {
    const h1Texts = headingsList.filter((h) => h.level === 1).map((h) => `"${h.text.slice(0, 50)}"`).join(', ');
    issues.push({
      severity: 'warning',
      message: `${h1Count} H1 détectés (${h1Texts}) — Google pourrait être confus sur le sujet principal.`,
      fix: 'Conservez un seul H1 et transformez les autres en H2.',
    });
  } else {
    strengths.push('Un seul H1 présent ✓');
  }

  if (h1Count > 0 && h2Count === 0 && h3Count > 0) {
    issues.push({
      severity: 'warning',
      message: 'Des H3 sont utilisés sans H2 — la hiérarchie n\'est pas respectée.',
      fix: 'Ajoutez des H2 pour structurer le contenu entre le H1 et les H3.',
    });
  }

  if (headingsList.length > 1) {
    let prevLevel = 0;
    const skips = [];
    for (const h of headingsList) {
      if (prevLevel > 0 && h.level > prevLevel + 1) {
        skips.push({ from: prevLevel, to: h.level, text: h.text });
      }
      prevLevel = h.level;
    }
    if (skips.length > 0) {
      issues.push({
        severity: 'warning',
        message: `${skips.length} saut(s) de niveau détecté(s) : ${skips.slice(0, 2).map((s) => `H${s.from} → H${s.to} ("${s.text.slice(0, 30)}…")`).join(', ')}`,
        fix: 'Respectez la hiérarchie H1 → H2 → H3 sans sauter de niveaux.',
      });
    }
  }

  if (h2Count >= 2) strengths.push(`${h2Count} H2 structurent bien le contenu ✓`);
  if (h3Count >= 2 && h2Count > 0) strengths.push('Sous-sections H3 bien utilisées ✓');

  const emptyHeadings = headingsList.filter((h) => !h.text || h.text.trim() === '');
  if (emptyHeadings.length > 0) {
    issues.push({
      severity: 'warning',
      message: `${emptyHeadings.length} titre(s) vide(s) détecté(s) — Google les ignore.`,
      fix: 'Ajoutez du texte descriptif ou supprimez les balises vides.',
    });
  }

  const h1 = headingsList.find((h) => h.level === 1);
  if (h1?.text) {
    if (h1.text.length < 10) {
      contentRecs.push({
        type: 'content',
        message: `Le H1 "${h1.text}" est très court. Un H1 descriptif aide Google et les utilisateurs à comprendre le sujet de la page.`,
        suggestion: 'Reformulez le H1 pour qu\'il décrive clairement le contenu de la page (20-70 caractères idéalement).',
      });
    }
    if (h1.text.length > 70) {
      contentRecs.push({
        type: 'content',
        message: `Le H1 "${h1.text.slice(0, 50)}…" est long (${h1.text.length} car.). Google pourrait le tronquer dans les résultats.`,
        suggestion: 'Raccourcissez le H1 à 60-70 caractères maximum en gardant les mots-clés importants.',
      });
    }
  }

  const h2s = headingsList.filter((h) => h.level === 2);
  if (h2s.length > 0) {
    const h2Texts = h2s.map((h) => h.text.toLowerCase().trim());
    const duplicates = h2Texts.filter((text, i) => h2Texts.indexOf(text) !== i);
    if (duplicates.length > 0) {
      contentRecs.push({
        type: 'content',
        message: `Titres H2 en double détectés : "${duplicates[0]}". Les sections devraient avoir des titres uniques.`,
        suggestion: 'Différenciez chaque H2 pour que Googlebot puisse distinguer chaque section.',
      });
    }

    const h2Starts = h2s.map((h) => h.text.split(' ').slice(0, 2).join(' ').toLowerCase());
    const repeatedStarts = h2Starts.filter((s, i) => h2Starts.filter((x) => x === s).length > 2);
    if (repeatedStarts.length > 0 && new Set(repeatedStarts).size > 0) {
      contentRecs.push({
        type: 'content',
        message: 'Plusieurs H2 commencent de la même manière — cela peut donner une impression de contenu répétitif.',
        suggestion: 'Variez la formulation de vos H2 pour un meilleur engagement utilisateur.',
      });
    }
  }

  if (h2s.length > 0 && headingsList.length > 5) {
    const sectionsWithoutSubheadings = [];
    for (let i = 0; i < headingsList.length; i++) {
      if (headingsList[i].level === 2) {
        const nextIdx = headingsList.findIndex((h, j) => j > i && h.level <= 2);
        const hasSubHeadings = headingsList
          .slice(i + 1, nextIdx === -1 ? undefined : nextIdx)
          .some((h) => h.level > 2);
        if (!hasSubHeadings && headingsList[i].text.length > 5) {
          sectionsWithoutSubheadings.push(headingsList[i].text);
        }
      }
    }
    if (sectionsWithoutSubheadings.length >= 3 && h3Count === 0) {
      contentRecs.push({
        type: 'seo',
        message: `${sectionsWithoutSubheadings.length} sections H2 n'ont aucun sous-titre (H3). Cela peut indiquer un contenu peu structuré.`,
        suggestion: 'Ajoutez des H3 dans vos sections longues pour améliorer la lisibilité et le crawl SEO.',
      });
    }
  }

  const genericTerms = ['section', 'partie', 'suite', 'contenu', 'texte', 'info', 'détail', 'more', 'click'];
  const genericHeadings = headingsList.filter((h) => {
    const lower = h.text.toLowerCase();
    return genericTerms.some((t) => lower === t || (lower.length < 15 && lower.includes(t)));
  });
  if (genericHeadings.length > 0) {
    contentRecs.push({
      type: 'seo',
      message: `${genericHeadings.length} titre(s) trop générique(s) : "${genericHeadings[0].text}". Googlebot et les utilisateurs ne peuvent pas comprendre le contenu de cette section.`,
      suggestion: 'Utilisez des titres descriptifs avec des mots-clés pertinents au lieu de termes génériques.',
    });
  }

  if (h1?.text && h1.text.length >= 20 && h1.text.length <= 70) {
    strengths.push(`H1 bien formulé (${h1.text.length} car.) ✓`);
  }
  if (h2s.length >= 3 && h2s.every((h) => h.text.length >= 10)) {
    strengths.push('H2 descriptifs et variés ✓');
  }

  const hasIssues = issues.some((i) => i.severity === 'error');
  const hasWarnings = issues.some((i) => i.severity === 'warning');

  return { issues, strengths, contentRecs, hasIssues, hasWarnings };
}

/** Libellé court pour badge (pages de référence). */
export function getHeadingsStructureStatus(headingsList, headingsCounts) {
  const total = headingsList.length;
  if (total === 0 && !(headingsCounts.h1 || headingsCounts.h2)) {
    return { tone: 'warning', label: 'Non analysée', ok: false, hasData: false };
  }
  const { hasIssues, hasWarnings, issues } = analyzeHeadings(headingsList, headingsCounts);
  if (hasIssues) {
    const n = issues.filter((i) => i.severity === 'error').length || issues.length;
    return { tone: 'error', label: `${n} problème${n > 1 ? 's' : ''}`, ok: false, hasData: true };
  }
  if (hasWarnings) {
    return { tone: 'warning', label: 'À vérifier', ok: false, hasData: true };
  }
  return { tone: 'ok', label: 'OK', ok: true, hasData: true };
}
